import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import classnames from "classnames";
import cloneDeep from "lodash.clonedeep";
import { fabric } from "sunzi-fabric";
import { Font, Shop } from "@/sunzi";
import {
  i18n,
  loadFont,
  loadHTMLImage,
  loadImageFromURL,
  loadSVGFromURL,
  thumbnail7n,
  getBlobUrl,
} from "@/utils";
import { fabricConfig } from "@/config";
import { Header, Loading } from "@/components";
import { PreviewType, TaskStatus, getKeyPoint, getFaceData } from ".";
import {
  CanvasData,
  FactoryData,
  MiniMeImage,
  MiniMeLineText,
  UserImage,
  Color,
  ColorVariant,
  ColorOption,
} from "./mini-me";
import TextEdit from "./text-edit";
import ColorOptions from "./color-options";
import styles from "./style.less";

const { mirror, scale, edit, rotate, camera, deleteImage } =
  fabricConfig.control;
const { borderColor, borderScaleFactor, cornerSize, textPadding } =
  fabricConfig.attribute;

/** 用户头像对象 */
type AvatarObject = {
  id: string | number;
  image: string;
};

interface PreviewProps {
  /** 商铺信息 */
  shop: Shop;
  /** 产品标题 */
  title: string;
  /** 价格 */
  price: string;
  /** 用户图片 */
  images: UserImage[];
  /** 预览页状态 */
  previewType: PreviewType;
  /** 卡通图片数据 */
  miniMeImage: MiniMeImage;
  /** 可刻面数 */
  sideCount: number;
  /** 刻字相关数据 */
  lineText?: MiniMeLineText;
  /** 当前处理的面数 */
  currentSide: MutableRefObject<number>;
  /** 上一次处理的面数 */
  prevSide: MutableRefObject<number>;
  /** 工厂数据 */
  factoryData: FactoryData | undefined;
  /** 画布数据 */
  canvasData: MutableRefObject<CanvasData[]>;
  /** 颜色 */
  selectedColor: Color;
  /** 颜色变体 */
  colorVariants?: ColorVariant[];
  /** 默认模板id */
  defaultTemplateId?: number | string;
  handleChangeIsFirst?: () => void;
  /** 增值服务弹窗判断 */
  handleIncrementDrawer: (factoryData: FactoryData) => void;
  /** 加载分享 */
  loadShareImage: () => void;
  /** 设置颜色变体数据 */
  setColorVariantsValue?: (values: ColorOption[]) => void;
  /** 设置当前处理的面数 */
  setCurrentSide: (side: number) => void;
  /** 设置工厂数据 */
  setFactoryData: (data: FactoryData) => void;
  /** 设置加价抽屉弹窗显影 */
  setIncrementDrawerVisible: Dispatch<SetStateAction<boolean>>;
  /** 设置图片确认弹窗显影 */
  setImageDrawerVisible: Dispatch<SetStateAction<boolean>>;
  /** 设置 edit 组件显影 */
  setEditVisible: Dispatch<SetStateAction<boolean>>;
  /** 设置 zuploader 组件显影 */
  setZuploaderVisible: Dispatch<SetStateAction<boolean>>;
  /** 设置任务状态 */
  setTaskStatus: Dispatch<SetStateAction<TaskStatus>>;
  /** 设置用户图片 */
  setImages: Dispatch<SetStateAction<UserImage[]>>;
  /** 设置预览页状态 */
  setPreviewType: Dispatch<SetStateAction<PreviewType>>;
  /** 谷歌分析 */
  gtag: any;
}

const Preview: React.FC<PreviewProps> = ({
  shop,
  title,
  price,
  images,
  previewType,
  miniMeImage,
  sideCount,
  lineText,
  currentSide,
  prevSide,
  factoryData,
  canvasData,
  selectedColor,
  colorVariants,
  defaultTemplateId,
  handleChangeIsFirst,
  handleIncrementDrawer,
  loadShareImage,
  setCurrentSide,
  setFactoryData,
  setColorVariantsValue,
  setIncrementDrawerVisible,
  setImageDrawerVisible,
  setEditVisible,
  setZuploaderVisible,
  setTaskStatus,
  setImages,
  setPreviewType,
  gtag,
}) => {
  /** 底图 fabric 对象 */
  const _bottomImage = useRef<fabric.Image>();
  /** 配件 fabric 对象列表 */
  const _annexImages = useRef<{ [propName: string]: fabric.Image }>({});
  /** canvas 包装 DOM */
  const _canvasWrapper = useRef<HTMLDivElement>(null);
  /** canvas DOM */
  const _element = useRef<HTMLCanvasElement>(null);
  /** fabric canvas 对象 */
  const _canvas = useRef<fabric.Canvas>();
  /** 刻字 fabric 对象 */
  const _lineText = useRef<fabric.Text>();
  /** multiple */
  const _multiple = useRef<number>(0);
  /** 已选择的修改图或头像 */
  const [selectedEdit, setSelectedEdit] = useState<number | string | undefined>(
    undefined
  );
  /** 字体选择弹窗显影 */
  const [textEditVisible, setTextEditVisible] = useState<boolean>(false);
  /** 刻字信息 */
  const [text, setText] = useState<string>("");
  /** 字体选择 */
  const [fontSelected, setFontSelected] = useState<Font>();
  /** 加载状态 */
  const [loading, setLoading] = useState<boolean>(false);
  /** 颜色选择显隐，肤色，发色，胡子颜色选择 */
  const [colorOptionsVisible, setColorOptionsVisible] = useState(false);
  /** 当前处理的头像 */
  const currentAvatar = images[images.length - 1];
  /** 配件和头像列表 */
  const annexImages = miniMeImage.annex?.map((item) => {
    const url = item.images.find(
      (image) => image.color === selectedColor.name
    )!.url;
    return {
      id: item.id,
      image: url,
    };
  });
  const optionList: AvatarObject[] = [
    ...(annexImages ?? []),
    { id: "avatar", image: currentAvatar.image as string },
  ];

  /** 设置 fabric 活动对象 */
  const setActiveObject = (id: any) => {
    setSelectedEdit(id);
    if (id !== "avatar") {
      _annexImages.current[id].opacity = 1;
      _annexImages.current[id].selectable = true;
      _annexImages.current[id].evented = true;
      _canvas.current?.bringToFront(_annexImages.current[id]);
    }
    _canvas.current?.setActiveObject(_annexImages.current[id]);
    _canvas.current?.renderAll();
  };

  /** 设置控制器 */
  const setControl = async () => {
    // 设置默认控制器图标
    if (_canvas.current) {
      const [tl, tr, bl, br] = await Promise.all([
        loadHTMLImage(camera),
        loadHTMLImage(scale),
        loadHTMLImage(mirror),
        loadHTMLImage(rotate),
      ]);
      _canvas.current.setControlImage("tl", tl);
      _canvas.current.setControlImage("tr", tr);
      _canvas.current.setControlImage("bl", bl);
      _canvas.current.setControlImage("br", br);
    }
  };

  /** 渲染 svg path 切割层 */
  const renderAvatarPath = async () => {
    const { type, avatar } = miniMeImage;
    if (type === "hood" && avatar.path && _canvas.current) {
      const canvasWidth = _canvas.current.getWidth();
      const canvasHeight = _canvas.current.getHeight();
      const pathImage = await loadSVGFromURL(avatar.path);
      const pathLeft =
        ((pathImage.left as number) / miniMeImage.width) * canvasWidth;
      const pathTop =
        ((pathImage.top as number) / miniMeImage.height) * canvasHeight;
      pathImage
        .set({
          fill: "transparent",
          selectable: false,
          left: pathLeft,
          top: pathTop,
          originY: "top",
          originX: "left",
        })
        .scale(
          Math.min(
            canvasWidth / miniMeImage.width,
            canvasHeight / miniMeImage.height
          )
        );
      return pathImage;
    } else {
      return undefined;
    }
  };

  /** 渲染身体图片 */
  const renderBodyImage = async () => {
    if (_canvas.current) {
      const canvasWidth = _canvas.current.getWidth();
      const canvasHeight = _canvas.current.getHeight();
      const imageOriginURL = miniMeImage.images.find(
        (item) => item.color === selectedColor.name
      )!.url;
      const imageURL = thumbnail7n(imageOriginURL, 600);
      const bodyImage = await loadImageFromURL(imageURL);
      const scale = Math.min(
        canvasWidth / (bodyImage.width as number),
        canvasHeight / (bodyImage.height as number)
      );
      // 衣服
      bodyImage.set({
        left: canvasWidth / 2,
        top: canvasHeight,
        originY: "bottom",
        originX: "center",
        scaleX: scale,
        scaleY: scale,
        selectable: false,
        hoverCursor: "default",
      });
      _canvas.current.add(bodyImage);
      // 保存工厂输出数据
      const factoryData = {
        makeWidth: miniMeImage.width,
        makeHeight: miniMeImage.height,
        layoutWidth: _canvas.current.getWidth(),
        layoutHeight: _canvas.current.getHeight(),
        color: selectedColor,
        bodyImage: imageOriginURL,
        isBodyFlip: miniMeImage.isBodyFlip ?? false,
      };
      setFactoryData(factoryData);
    }
  };

  /** 渲染人像 */
  const renderAvatar = async (
    avatarImage: fabric.Image,
    pathImage?: fabric.Object
  ) => {
    const { face, avatar } = miniMeImage;
    const canvasWidth = _canvas.current!.getWidth();
    const canvasHeight = _canvas.current!.getHeight();

    if (prevSide.current > currentSide.current) {
      // 如果是从 B 面返回到 A 面，则获取上次保存的数据渲染
      const imageData = canvasData.current[currentSide.current - 1].image;
      avatarImage.set({
        id: "avatar",
        left: imageData?.avatar.left,
        top: imageData?.avatar.top,
        scaleX: imageData?.avatar.scaleX,
        scaleY: imageData?.avatar.scaleY,
        angle: imageData?.avatar.rotate,
        flipX: imageData?.avatar.flipX,
        flipY: imageData?.avatar.flipY,
        originY: "center",
        originX: "center",
        selectable: true,
        borderColor,
        borderScaleFactor: borderScaleFactor * _multiple.current,
        cornerSize: cornerSize * _multiple.current,
        onTLPress: () => setZuploaderVisible(true),
        onBLPress: (target: fabric.Object) => {
          target.flipX = !target.flipX;
          _canvas.current?.renderAll();
        },
      });
    } else {
      let avatarLeft: number = 0;
      let avatarTop: number = 0;
      let scale: number = 0;
      // 如果是新绘制的页面，则直接创建
      if (currentAvatar.faceRecognizeData && face) {
        // 获取人脸识别数据
        const { faceRect } = currentAvatar.faceRecognizeData;
        const faceWidth = (face.width / miniMeImage.width) * canvasWidth;
        const faceHeight = (face.height / miniMeImage.height) * canvasHeight;
        scale = Math.min(
          faceWidth / faceRect.width,
          faceHeight / faceRect.height
        );
        const heightDistance =
          ((avatarImage.height as number) - faceRect.top - faceRect.height) *
          scale;
        avatarTop =
          ((face.top + face.height) / miniMeImage.height) * canvasHeight +
          heightDistance;
        const avatarWidth = (avatarImage.width as number) * scale;
        avatarLeft =
          (face.left / miniMeImage.width) * canvasWidth -
          faceRect.left * scale +
          avatarWidth / 2;
      } else {
        // 如果人脸识别失败，则按照给定数据渲染图片
        const avatarWidth = (avatar.width / miniMeImage.width) * canvasWidth;
        const avatarHeight =
          (avatar.height / miniMeImage.height) * canvasHeight;
        scale = Math.min(
          avatarWidth / (avatarImage.width ?? 0),
          avatarHeight / (avatarImage.height ?? 0)
        );
        avatarLeft =
          ((avatar.left + avatar.width / 2) / miniMeImage.width) * canvasWidth;
        avatarTop =
          ((avatar.top + avatar.height) / miniMeImage.height) * canvasHeight;
      }
      avatarImage.set({
        id: "avatar",
        left: avatarLeft,
        top: avatarTop,
        originX: "center",
        originY: "bottom",
        scaleX: scale,
        scaleY: scale,
        selectable: true,
        borderColor,
        borderScaleFactor: borderScaleFactor * _multiple.current,
        cornerSize: cornerSize * _multiple.current,
        onTLPress: () => setZuploaderVisible(true),
        onBLPress: (target: fabric.Object) => {
          target.flipX = !target.flipX;
          _canvas.current?.renderAll();
        },
      });
    }

    if (pathImage) {
      avatarImage.set({
        clipTo: (ctx: CanvasRenderingContext2D) => {
          ctx.save();
          pathImage.set({
            objectCaching: false,
          });
          ctx.setTransform(
            window.devicePixelRatio,
            0,
            0,
            window.devicePixelRatio,
            0,
            0
          );
          pathImage.render(ctx);
          ctx.restore();
        },
      });
    }
    // 解决默认 fabric 存在的旋转图标的 bug
    avatarImage.setControlVisible("mtr", false);
    _canvas.current!.add(avatarImage);
  };

  /** 渲染配件 */
  const renderAnnex = async (deleteImg: HTMLImageElement) => {
    const { annex, face } = miniMeImage;
    const canvasWidth = _canvas.current!.getWidth();
    const canvasHeight = _canvas.current!.getHeight();
    if (annex && annex.length > 0) {
      // 获取配件压缩后的图片地址
      const annexsOriginURL = annex.map(
        (item) =>
          item.images.find((image) => image.color === selectedColor.name)!.url
      );
      const annexsURL = annexsOriginURL.map((item) => thumbnail7n(item, 200));
      // 加载配件
      const annexImages = await Promise.all(
        annexsURL.map(async (item) => loadImageFromURL(item))
      );
      if (prevSide.current > currentSide.current) {
        annexImages.forEach((item, index) => {
          const currentAnnex = annex[index];
          _annexImages.current[currentAnnex.id] = item;
          const annexData = canvasData.current[
            currentSide.current - 1
          ].image?.annexs?.find((item) => item.id === currentAnnex.id);
          item.setControlImage("tl", deleteImg);
          item.set({
            id: currentAnnex.id,
            left: annexData?.left,
            top: annexData?.top,
            scaleX: annexData?.scaleX,
            scaleY: annexData?.scaleY,
            angle: annexData?.rotate,
            flipX: annexData?.flipX,
            flipY: annexData?.flipY,
            originY: "center",
            originX: "center",
            selectable: true,
            borderColor,
            borderScaleFactor: borderScaleFactor * _multiple.current,
            cornerSize: cornerSize * _multiple.current,
            onBLPress: (target: fabric.Object) => {
              target.flipX = !target.flipX;
              _canvas.current?.renderAll();
            },
            onTLPress: (target: fabric.Object) => {
              target.opacity = 0;
              target.selectable = false;
              target.evented = false;
              _canvas.current?.sendToBack(target);
              _canvas.current?.discardActiveObject();
              setSelectedEdit(undefined);
              _canvas.current?.renderAll();
            },
          });
          _canvas.current?.add(item);
        });
      } else {
        // 获取头像 fabric 对象
        const avatarImage = _annexImages.current["avatar"];

        annexImages.forEach((item, index) => {
          const currentAnnex = annex[index];
          _annexImages.current[currentAnnex.id] = item;
          item.setControlImage("tl", deleteImg);

          let annexTop = 0;
          let annexLeft = 0;
          let annexScale = 0;
          // 如果人脸识别成功就适配配件
          if (currentAvatar.faceRecognizeData && face) {
            // 获取人脸识别数据
            const { faceRect } = currentAvatar.faceRecognizeData;
            const { leftPoint, rightPoint, feature } = currentAnnex.faceFit;
            const avatarImageScale = avatarImage.scaleX as number;
            // 实际图片的脸区域的宽高
            const avatarFaceWidth = faceRect.width * avatarImageScale;
            // 脸部安全区大小
            const faceFitWidth = (face.width / miniMeImage.width) * canvasWidth;
            // 脸部安全区坐标
            const faceFitLeft = (face.left / miniMeImage.width) * canvasWidth;
            // 配件图片缩放比
            const scale = (item.width as number) / currentAnnex.width;
            // 拿到关键点坐标
            const keyPoint = getKeyPoint(currentAvatar, feature);
            console.log('currentAnnex.faceFit', currentAnnex.faceFit, keyPoint);

            const faceLeft = faceFitLeft + (faceFitWidth - avatarFaceWidth) / 2;
            annexScale =
              avatarFaceWidth / ((rightPoint.left - leftPoint.left) * scale);
            if (feature === "head") {
              annexTop =
                avatarImage._getLeftTopCoords().y +
                keyPoint.y * avatarImageScale -
                100 * avatarImageScale -
                leftPoint.top * scale * annexScale +
                (currentAnnex.height / 2) * scale * annexScale;
            } else {
              annexTop =
                avatarImage._getLeftTopCoords().y +
                keyPoint.y * avatarImageScale -
                leftPoint.top * scale * annexScale +
                (currentAnnex.height / 2) * scale * annexScale;
            }
            annexLeft =
              faceLeft -
              leftPoint.left * scale * annexScale +
              (currentAnnex.width / 2) * scale * annexScale;
          } else {
            annexLeft =
              ((currentAnnex.left + currentAnnex.width / 2) /
                miniMeImage.width) *
              canvasWidth;
            annexTop =
              ((currentAnnex.top + currentAnnex.height / 2) /
                miniMeImage.height) *
              canvasHeight;
            const annexWidth =
              (currentAnnex.width / miniMeImage.width) * canvasWidth;
            const annexHeight =
              (currentAnnex.height / miniMeImage.height) * canvasHeight;
            annexScale = Math.min(
              annexWidth / (item.width ?? 0),
              annexHeight / (item.height ?? 0)
            );
          }
          item.set({
            id: currentAnnex.id,
            left: annexLeft,
            top: annexTop,
            originX: "center",
            originY: "center",
            scaleX: annexScale,
            scaleY: annexScale,
            selectable: true,
            borderColor,
            borderScaleFactor: borderScaleFactor * _multiple.current,
            cornerSize: cornerSize * _multiple.current,
            onBLPress: (target: fabric.Object) => {
              target.flipX = !target.flipX;
              _canvas.current?.renderAll();
            },
            onTLPress: (target: fabric.Object) => {
              target.opacity = 0;
              target.selectable = false;
              target.evented = false;
              _canvas.current?.sendToBack(target);
              _canvas.current?.discardActiveObject();
              setSelectedEdit(undefined);
              _canvas.current?.renderAll();
            },
          });
          // 解决 fabric 默认存在旋转 bug 的问题
          item.setControlVisible("mtr", false);
          _canvas.current?.add(item);
        });
      }
    }
  };

  /** 渲染卡通人像 */
  const renderMiniMeImage = async () => {
    if (_canvas.current && miniMeImage) {
      // 转 bloburl 解决 safari 不显示的问题
      const avatarUrl = await getBlobUrl(currentAvatar.image!);
      const [avatarImage, deleteImg, pathImage] = await Promise.all([
        loadImageFromURL(avatarUrl),
        loadHTMLImage(deleteImage),
        renderAvatarPath(),
        renderBodyImage(),
      ]);
      URL.revokeObjectURL(avatarUrl);
      _annexImages.current["avatar"] = avatarImage;
      await renderAvatar(avatarImage, pathImage);
      await renderAnnex(deleteImg);
      // 默认选中头部
      setActiveObject("avatar");
    }
  };

  /** 渲染刻字 */
  const renderLineText = async () => {
    if (lineText && _bottomImage.current && _canvas.current) {
      try {
        await Promise.all(
          lineText.fonts.map(async (item) => await loadFont(item.name))
        );
      } catch (e) {
        console.error(`loadfont error`);
      }
      const canvasWidth = _canvas.current.getWidth();
      const canvasHeight = _canvas.current.getHeight();
      // 刻字
      const tl = await loadHTMLImage(edit);
      _lineText.current = new fabric.Text(lineText.defaultText ?? text, {
        id: "lineText",
        // left: lineText.left,
        // top: lineText.top,
        left: canvasWidth / 2,
        top: canvasHeight / 2,
        originX: "center",
        originY: "center",
        selectable: true,
        // width: lineText.width,
        // height: lineText.height,
        fontSize: lineText.fontSize,
        fill: lineText.fontColor,
        fontFamily: lineText.fonts[0].name,
        lineHeight: 1,
        borderColor,
        padding: textPadding * _multiple.current,
        borderScaleFactor: borderScaleFactor * _multiple.current,
        cornerSize: cornerSize * _multiple.current,
        onTLPress: (target: fabric.Object) => {
          setTextEditVisible(true);
        },
      });
      _lineText.current.setControlImage("tl", tl);
      _lineText.current.setControlVisible("bl", false);
      _lineText.current.setControlVisible("br", false);
      // 解决 fabric 默认存在旋转 bug 的问题
      _lineText.current.setControlVisible("mtr", false);
      _canvas.current?.add(_lineText.current);
      // 钥匙扣背景图
      _bottomImage.current
        .set({
          left: canvasWidth / 2,
          top: canvasHeight,
          originY: "bottom",
          originX: "center",
          selectable: false,
          evented: false,
          hoverCursor: "default",
        })
        .scale(
          Math.min(
            canvasWidth / (_bottomImage.current.width ?? 0),
            canvasHeight / (_bottomImage.current.height ?? 0)
          )
        );
      _canvas.current?.add(_bottomImage.current);
      // 设置刻字对象为选中
      _canvas.current?.setActiveObject(_lineText.current);
      _canvas.current?.renderAll();
    }
  };

  /** 画布初始化 */
  const canvasInit = async () => {
    if (_element.current && _canvasWrapper.current) {
      setLoading(true);
      // 如果没有加载分享图，就加载
      await loadShareImage();
      const { offsetWidth } = _canvasWrapper.current;
      // 如果 canvas 已经创建就清除画布，如果没创建就新建一个新的画布
      if (_canvas.current) {
        _canvas.current?.clear();
        // 重新进入这个页面重新刷新一次人脸识别
        const faceRecognizeData = await getFaceData(currentAvatar.image!);
        currentAvatar.faceRecognizeData = faceRecognizeData;
      } else {
        _canvas.current = new fabric.Canvas(_element.current, {
          selection: false,
          preserveObjectStacking: true,
        });
      }
      // 绑定事件
      _canvas.current.on({
        "mouse:down": (e: fabric.IEvent) => {
          if (e.target?.id) {
            if (e.target.id !== "lineText" && e.target.opacity !== 0) {
              setActiveObject(e.target.id);
            }
          } else {
            _canvas.current?.discardActiveObject();
            setSelectedEdit(undefined);
          }
        },
      });

      // set multiple
      _multiple.current = Math.min(offsetWidth / 375, offsetWidth / 375);
      if (previewType === PreviewType.Image) {
        // 卡通图片定制初始化
        // set css and backstore dimensions
        _canvas.current.setDimensions({
          width: offsetWidth,
          height: (miniMeImage.height / miniMeImage.width) * offsetWidth,
        });
        // set canvas backstore dimensions
        _canvas.current.setDimensions(
          {
            width: offsetWidth,
            height: (miniMeImage.height / miniMeImage.width) * offsetWidth,
          },
          { backstoreOnly: true }
        );
        // 设置默认控制器图标
        await setControl();
        // 渲染卡通人
        await renderMiniMeImage();
      } else if (previewType === PreviewType.Text && lineText) {
        // 刻字定制初始化
        const bottomImage = await loadImageFromURL(lineText.backgroundImage);
        _bottomImage.current = bottomImage;
        const bottomImageHeight = bottomImage.height as number;
        const bottomImageWidth = bottomImage.width as number;
        // set css and backstore dimensions
        _canvas.current.setDimensions({
          width: offsetWidth,
          height: (bottomImageHeight / bottomImageWidth) * offsetWidth,
        });
        // set canvas backstore dimensions
        _canvas.current.setDimensions(
          {
            width: offsetWidth,
            height: (bottomImageHeight / bottomImageWidth) * offsetWidth,
          },
          { backstoreOnly: true }
        );
        // 设置默认控制器图标
        await setControl();
        // set multiple
        _multiple.current = Math.min(offsetWidth / 375, offsetWidth / 375);
        // 设置选中的字体
        setFontSelected(lineText.fonts[0]);
        // 渲染刻字
        await renderLineText();
      }
      setLoading(false);
    }
  };

  /** 添加当前数据至保存的画布数据中 */
  const addCanvasData = () => {
    if (miniMeImage && previewType === PreviewType.Image) {
      const avatarImage = _annexImages.current["avatar"];
      const annex = Object.keys(_annexImages.current)
        .filter(
          (item) =>
            item !== "avatar" && _annexImages.current[item].opacity !== 0
        )
        .map((item) => {
          const currentAnnex = _annexImages.current[item];
          const annexData = miniMeImage.annex?.find(
            (element) => currentAnnex.id === element.id
          );
          const imageURL = annexData!.images.find(
            (item) => item.color === selectedColor.name
          )!.url;
          return {
            id: currentAnnex.id as number,
            left: currentAnnex.left as number,
            top: currentAnnex.top as number,
            width: currentAnnex.width as number,
            height: currentAnnex.height as number,
            image: imageURL,
            scaleX: currentAnnex.scaleX as number,
            scaleY: currentAnnex.scaleY as number,
            rotate: currentAnnex.angle as number,
            flipX: currentAnnex.flipX as boolean,
            flipY: currentAnnex.flipY as boolean,
            isFlip: annexData?.isFlip,
          };
        });
      const centerPoint = avatarImage.getCenterPoint();
      const avatarTop = centerPoint.y;
      const avatarLeft = centerPoint.x;
      canvasData.current[currentSide.current - 1] = {
        image: {
          id: miniMeImage.id,
          type: miniMeImage.type,
          avatar: {
            left: avatarLeft,
            top: avatarTop,
            width: avatarImage.width as number,
            height: avatarImage.height as number,
            scaleX: avatarImage.scaleX as number,
            scaleY: avatarImage.scaleY as number,
            rotate: avatarImage.angle as number,
            flipX: avatarImage.flipX as boolean,
            flipY: avatarImage.flipY as boolean,
            path: miniMeImage.avatar.path,
            image: currentAvatar.image as string,
            originImage: images[images.length - 1].source,
          },
          annexs: annex,
          compose: _canvas.current!.getElement().toDataURL(),
        },
      };
    } else if (
      previewType === PreviewType.Text &&
      lineText &&
      _lineText.current
    ) {
      canvasData.current[currentSide.current - 1] = {
        lineText: {
          left: _lineText.current.left ?? lineText.left,
          top: _lineText.current.top ?? lineText.top,
          width: _lineText.current.width ?? lineText.width,
          height: _lineText.current.height ?? lineText.height,
          fontSize: _lineText.current.fontSize ?? lineText.fontSize,
          fontColor: lineText.fontColor,
          font: fontSelected as Font,
          scaleX: _lineText.current.scaleX ?? 0,
          scaleY: _lineText.current.scaleY ?? 0,
          rotate: _lineText.current.angle ?? 0,
          flipX: _lineText.current.flipX ?? false,
          flipY: _lineText.current.flipY ?? false,
          values: _lineText.current.text ?? "",
          compose: _canvas.current!.getElement().toDataURL(),
        },
      };
    }
  };

  /** 刻字确定 */
  const handleTextEditConfirm = () => {
    setTextEditVisible(false);
    _lineText.current?.setOptions({
      text: text,
      fontFamily: fontSelected!.name,
    });
    _canvas.current?.renderAll();
  };

  /** 点击关闭 */
  const handleClose = () => {
    if (currentSide.current > 1) {
      // 如果上一个面是图片，则切换画图状态，反之切换文字状态
      // 数组是从 0 开始，所以要 -2
      if (canvasData.current[currentSide.current - 2].hasOwnProperty("image"))
        setPreviewType(PreviewType.Image);
      else setPreviewType(PreviewType.Text);
      // 如果当前处理的面数大于 1（比如 B 面)，则返回上一个处理的面
      setCurrentSide(currentSide.current - 1);
      // 如果当前是处理图片则删除当前图片
      if (previewType === PreviewType.Image) {
        images.pop();
        if (canvasData.current.length > currentSide.current) {
          canvasData.current.pop();
        }
        setImages(cloneDeep(images));
      }
    } else if (defaultTemplateId) {
      // 如果存在模板id，返回上传图片页
      setZuploaderVisible(true);
      handleChangeIsFirst && handleChangeIsFirst();
    } else {
      // 如果当前处理的是第一个面（比如 A 面）,则返回选择页面
      setTaskStatus(TaskStatus.Choose);
    }
  };

  /** 点击确定 */
  const handleConfirm = () => {
    // 如果有多个面就弹窗
    _canvas.current!.discardActiveObject();
    _canvas.current!.renderAll();
    if (colorVariants && colorVariants.length > 0) {
      setColorOptionsVisible(true);
      return;
    }
    resolveDrawer();
    // if (currentSide.current < sideCount) {
    //   addCanvasData();
    //   setIncrementDrawerVisible(true);
    // } else {
    //   // 加车
    //   addCanvasData();
    //   if (!lineText && canvasData.current.length > 1) {
    //     setImageDrawerVisible(true);
    //   } else {
    //     handleIncrementDrawer(factoryData!);
    //   }
    // }
  };

  /** 肤色，发色，胡子颜色确认 */
  const handleColorOptionsConfirm = (values: ColorOption[]) => {
    setColorVariantsValue && setColorVariantsValue(values);
    resolveDrawer();
  };

  /** 处理后续弹窗 */
  const resolveDrawer = () => {
    if (currentSide.current < sideCount) {
      addCanvasData();
      setIncrementDrawerVisible(true);
    } else {
      // 加车
      addCanvasData();
      if (!lineText && canvasData.current.length > 1) {
        setImageDrawerVisible(true);
      } else {
        handleIncrementDrawer(factoryData!);
      }
    }
  };

  useEffect(() => {
    fabric.Object.prototype.objectCaching = false;
    canvasInit();
    setText("");
  }, [currentSide.current, previewType, images]);

  useEffect(() => {
    return () => {
      _canvas.current?.clear();
      _canvas.current?.dispose();
    };
  }, []);

  return (
    <div className={styles.preview}>
      <Header
        className={styles.previewHeader}
        closeIconType="left"
        closeIconColor="#000"
        onClose={handleClose}
        price={price}
        title={title}
        renderSubmit={
          <div
            className={classnames(styles.previewHeaderSubmit, {
              [styles.disable]:
                loading ||
                (previewType === PreviewType.Text &&
                  _lineText.current?.text === lineText?.defaultText),
            })}
            onClick={handleConfirm}
          >
            {i18n.format("modules.global.next")}
          </div>
        }
      />
      <div className={styles.previewContent}>
        {loading && (
          <div className={styles.previewContentLoading}>
            <Loading color="#ff533a" size={60} />
          </div>
        )}
        <div className={styles.canvasContent}>
          <div ref={_canvasWrapper} className={styles.canvasLayout}>
            <canvas ref={_element} className={styles.canvas} />
          </div>
        </div>
        {previewType === PreviewType.Image && (
          <div className={styles.editLayout}>
            {optionList.map((item) => (
              <div
                key={item.id}
                className={classnames(styles.editItem, {
                  [styles.editSelected]: selectedEdit === item.id,
                })}
                onClick={() => setActiveObject(item.id)}
              >
                <img src={item.image} />
                {selectedEdit === item.id && item.id === "avatar" && (
                  <div
                    className={styles.changeLayout}
                    onClick={() => setEditVisible(true)}
                  >
                    {i18n.format("modules.mini.me.replace.or.edit")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {textEditVisible && (
          <TextEdit
            lineText={lineText}
            text={text}
            fontSelected={fontSelected}
            setText={setText}
            setFontSelected={setFontSelected}
            onConfirm={handleTextEditConfirm}
          />
        )}
      </div>
      {colorOptionsVisible && (
        <ColorOptions
          onClose={() => setColorOptionsVisible(false)}
          onConfirm={handleColorOptionsConfirm}
          value={colorVariants || []}
        />
      )}
    </div>
  );
};

export default Preview;
